import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { apiRequest } from "../../api/http";
import { EmployeeDetail, EmployeeListItem, EmploymentStatus, PaginatedResponse } from "../../types/admin";

export type EmployeeListQuery = {
  branch_id?: number;
  region?: string;
  employment_status?: EmploymentStatus | "재직" | "퇴사";
  q?: string;
  page?: number;
  page_size?: number;
};

export type UpdateEmployeePayload = {
  employeeId: number;
  employment_status?: EmploymentStatus;
  weekly_work_minutes?: number;
};

type EmployeesState = {
  list: EmployeeListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  loadingList: boolean;
  listError: string | null;
  detail: EmployeeDetail | null;
  loadingDetail: boolean;
  detailError: string | null;
  updating: boolean;
  updateError: string | null;
  deleting: boolean;
  deleteError: string | null;
};

const initialState: EmployeesState = {
  list: [],
  totalCount: 0,
  page: 1,
  pageSize: 20,
  loadingList: false,
  listError: null,
  detail: null,
  loadingDetail: false,
  detailError: null,
  updating: false,
  updateError: null,
  deleting: false,
  deleteError: null,
};

const toQuery = (query: EmployeeListQuery): string => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      params.set(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

export const fetchEmployees = createAsyncThunk<
  PaginatedResponse<EmployeeListItem>,
  EmployeeListQuery,
  { rejectValue: string }
>("employees/fetchEmployees", async (query, { rejectWithValue }) => {
  try {
    return await apiRequest<PaginatedResponse<EmployeeListItem>>(`/admin/employees${toQuery(query)}`);
  } catch (error) {
    const message = error instanceof Error ? error.message : "직원 목록 조회에 실패했습니다.";
    return rejectWithValue(message);
  }
});

export const fetchEmployeeDetail = createAsyncThunk<EmployeeDetail, number, { rejectValue: string }>(
  "employees/fetchEmployeeDetail",
  async (employeeId, { rejectWithValue }) => {
    try {
      return await apiRequest<EmployeeDetail>(`/admin/employees/${employeeId}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : "직원 상세 조회에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

export const updateEmployee = createAsyncThunk<EmployeeDetail, UpdateEmployeePayload, { rejectValue: string }>(
  "employees/updateEmployee",
  async (payload, { rejectWithValue }) => {
    try {
      const { employeeId, ...body } = payload;
      return await apiRequest<EmployeeDetail>(`/admin/employees/${employeeId}`, {
        method: "PATCH",
        body,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "직원 정보 수정에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

export const deleteEmployee = createAsyncThunk<{ deleted: boolean; employee_id: number }, number, { rejectValue: string }>(
  "employees/deleteEmployee",
  async (employeeId, { rejectWithValue }) => {
    try {
      return await apiRequest<{ deleted: boolean; employee_id: number }>(`/admin/employees/${employeeId}`, {
        method: "DELETE",
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "직원 삭제에 실패했습니다.";
      return rejectWithValue(message);
    }
  }
);

const employeesSlice = createSlice({
  name: "employees",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployees.pending, (state) => {
        state.loadingList = true;
        state.listError = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.loadingList = false;
        state.list = action.payload.items;
        state.totalCount = action.payload.total_count;
        state.page = action.payload.page;
        state.pageSize = action.payload.page_size;
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.loadingList = false;
        state.listError = action.payload ?? "직원 목록 조회에 실패했습니다.";
      })
      .addCase(fetchEmployeeDetail.pending, (state) => {
        state.loadingDetail = true;
        state.detailError = null;
      })
      .addCase(fetchEmployeeDetail.fulfilled, (state, action) => {
        state.loadingDetail = false;
        state.detail = action.payload;
      })
      .addCase(fetchEmployeeDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.detailError = action.payload ?? "직원 상세 조회에 실패했습니다.";
      })
      .addCase(updateEmployee.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(updateEmployee.fulfilled, (state, action) => {
        state.updating = false;
        state.detail = action.payload;
      })
      .addCase(updateEmployee.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload ?? "직원 정보 수정에 실패했습니다.";
      })
      .addCase(deleteEmployee.pending, (state) => {
        state.deleting = true;
        state.deleteError = null;
      })
      .addCase(deleteEmployee.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteEmployee.rejected, (state, action) => {
        state.deleting = false;
        state.deleteError = action.payload ?? "직원 삭제에 실패했습니다.";
      });
  },
});

export default employeesSlice.reducer;
